/**
 * The diagram-geometry codec: the `=NN%` display width Niobe stores for a rendered diagram
 * (a ```` ```mermaid ```` or ```` ```svg ```` block) in the fenced code block's INFO STRING,
 * glued to the language with no space.
 *
 *     ```mermaid=60%
 *     graph TD; A-->B;
 *     ```
 *
 * This is the OUTER width — how much of the text column the diagram box takes. The zoom
 * control inside the box (`CodeBlockMermaid.tsx`) magnifies the drawing WITHIN that box and is
 * a view setting, private to one reader and deliberately not stored.
 *
 * WHY THE INFO STRING. Same constraint as an image (`image-geometry.ts`): the part IS its
 * markdown, re-serialised whole on every transaction and re-canonicalised server-side, so any
 * geometry the markdown cannot express is erased on the next reload. A code block has no title
 * slot; the only field it round-trips is the fence's language, which tiptap keeps verbatim in
 * `node.attrs.language` and writes back as-is.
 *
 * WHY NO SPACE BEFORE THE TOKEN. markdown-it keeps only the FIRST whitespace-separated word of
 * an info string (`langName = info.split(/(\s+)/g)[0]`) and emits just that as
 * `class="language-…"`, which is the only thing tiptap's CodeBlock parses back. So
 * ```` ```mermaid =60% ```` serialises fine and is gone on the next parse — checked against the
 * backend canonicaliser, which drops it. Glued to the language the whole word survives both
 * stacks untouched, exactly like the image title token.
 *
 * WHY NO BACKEND TWIN, unlike `image-geometry.ts`: nothing server-side reads a fence info
 * string. The canonicaliser carries `language` through verbatim (pinned by the
 * `mermaid-width-token` entry of `shared/md-canonical.fixture.json`) and every exporter drops
 * the fence line outright — `splitBlocks` in `adapters/export/markdown.ts` keeps a code block's
 * lines and nothing else — so there is no second implementation to hold in parity. The width
 * RANGE and rounding are imported from `image-geometry.ts` so a diagram and an image can never
 * disagree about what 5–100% means.
 *
 * A MALFORMED TOKEN is dropped, not preserved — the opposite of the image title, on purpose.
 * A title slot can hold prose a human typed and must never be destroyed; a fence info string
 * holds no prose. Keeping `mermaid=64000%` (what two peers resizing at once can interleave)
 * would mean a language that is no longer `mermaid`, i.e. a diagram that stops rendering. So
 * the language is recovered, the broken width is discarded, and the next commit repairs it.
 */

import { clampWidthPct, WIDTH_MAX, WIDTH_MIN } from './image-geometry'

/** A trailing `=<something>%` on the info word — the geometry slot, valid or not. */
const WIDTH_SUFFIX = /^(\S*?)=([^=\s]*)%$/
/** What a VALID width looks like inside that slot: 1–3 digits, at most one decimal. */
const WIDTH_VALUE = /^\d{1,3}(?:\.\d)?$/

/** A fenced code block's info string, split into what it means. */
export interface CodeLanguageParts {
  /** The language tiptap and the mermaid renderer match on — `mermaid`, `svg`, `ts`, ''. */
  language: string
  /** Display width in percent of the text column, or null when unset (natural size). */
  widthPct: number | null
}

/** Split a code block's `language` attribute into its language and its display width. */
export function parseCodeLanguage(raw: string | null | undefined): CodeLanguageParts {
  // Only the first word can survive a markdown round trip (markdown-it drops the rest of the
  // info string), so anything after a space is already lost — read what is durable.
  const word = (raw ?? '').trim().split(/\s+/)[0] ?? ''
  const suffix = WIDTH_SUFFIX.exec(word)
  if (!suffix) return { language: word, widthPct: null }
  const [, language, value] = suffix
  if (!WIDTH_VALUE.test(value ?? '')) return { language: language ?? '', widthPct: null }
  const pct = Number(value)
  if (pct < WIDTH_MIN || pct > WIDTH_MAX) return { language: language ?? '', widthPct: null }
  return { language: language ?? '', widthPct: pct }
}

/**
 * Build the info string from its parts. Returns null when there is nothing to say, so the
 * caller clears the attribute and the fence goes back to a bare ```` ``` ````.
 */
export function formatCodeLanguage(parts: CodeLanguageParts): string | null {
  // A space here would silently truncate on the next parse; never emit one.
  const language = (parts.language ?? '').trim().split(/\s+/)[0] ?? ''
  const width = parts.widthPct == null ? null : clampWidthPct(parts.widthPct)
  if (width === null) return language === '' ? null : language
  return `${language}=${width}%`
}
